import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';

const Background = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 2;
  }
`;

const VideoContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const VideoIframe = styled.iframe`
  width: 100vw;
  height: 56.25vw; /* 16:9 aspect ratio */
  min-height: 100vh;
  min-width: 177.77vh; /* 16:9 aspect ratio */
  border: none;
  pointer-events: none;
`;

const LocalVideo = styled.video<{ isDebugging?: boolean }>`
  width: 100vw;
  height: 56.25vw; /* 16:9 aspect ratio */
  min-height: 100vh;
  min-width: 177.77vh; /* 16:9 aspect ratio */
  object-fit: cover;
  pointer-events: none;
  z-index: 1;
  
  ${props => props.isDebugging && `
    border: 5px solid red !important;
    background: blue !important;
  `}
`;

const FallbackBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, #2a1a1a, #1a2a1a);
  z-index: 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.5) 100%);
  }
`;

const DebugInfo = styled.div`
  position: fixed;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px;
  font-size: 12px;
  z-index: 1000;
  border-radius: 4px;
`;

interface VideoBackgroundProps {
  platform: 'youtube' | 'vimeo' | 'local' | 'url';
  videoId: string;
  hash?: string; // For Vimeo private videos
  fallbackImage?: string;
}

const VideoBackground = ({ platform, videoId, hash, fallbackImage }: VideoBackgroundProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [debugInfo, setDebugInfo] = useState<string>('Initializing...');
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    if ((platform === 'local' || platform === 'url') && videoRef.current) {
      const video = videoRef.current;
      setDebugInfo('Video element found, setting up...');
      
      const playVideo = async () => {
        try {
          setDebugInfo('Attempting to play video...');
          // Ensure video is muted for autoplay policy compliance
          video.muted = true;
          video.volume = 0;
          await video.play();
          setDebugInfo('✅ Video is playing successfully!');
          setTimeout(() => setShowDebug(false), 3000); // Hide debug after 3 seconds
        } catch (error) {
          setDebugInfo(`❌ Error playing video: ${error}`);
          console.error('Error playing video:', error);
          
          // Fallback: try to play after user interaction
          const handleUserInteraction = async () => {
            try {
              await video.play();
              setDebugInfo('✅ Video started after user interaction');
              setTimeout(() => setShowDebug(false), 3000);
              document.removeEventListener('click', handleUserInteraction);
              document.removeEventListener('keydown', handleUserInteraction);
            } catch (e) {
              setDebugInfo(`❌ Failed even after interaction: ${e}`);
              console.error('Failed to play video even after user interaction:', e);
            }
          };
          
          document.addEventListener('click', handleUserInteraction);
          document.addEventListener('keydown', handleUserInteraction);
        }
      };

      // Wait for video to load metadata before attempting to play
      const handleLoadedMetadata = () => {
        setDebugInfo('Video metadata loaded, attempting play...');
        playVideo();
      };

      const handleLoadStart = () => {
        setDebugInfo('Video loading started...');
      };

      const handleCanPlay = () => {
        setDebugInfo('Video can play, ready to start...');
      };

      const handleError = (e: any) => {
        setDebugInfo(`❌ Video load error: ${e.target?.error?.message || 'Unknown error'}`);
        console.error('Video load error:', e);
      };

      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('loadstart', handleLoadStart);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('error', handleError);
      
      // If metadata is already loaded, play immediately
      if (video.readyState >= 1) {
        setDebugInfo('Video ready, playing immediately...');
        playVideo();
      }

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('loadstart', handleLoadStart);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('error', handleError);
      };
    }
  }, [platform]);

  const getEmbedUrl = () => {
    if (platform === 'youtube') {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playlist=${videoId}&enablejsapi=1&origin=${window.location.origin}`;
    } else if (platform === 'vimeo') {
      return hash 
        ? `https://player.vimeo.com/video/${videoId}?h=${hash}&autoplay=1&muted=1&loop=1&background=1&controls=0&title=0&byline=0&portrait=0`
        : `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&background=1&controls=0&title=0&byline=0&portrait=0`;
    }
    return '';
  };

  const renderVideo = () => {
    if (platform === 'local' || platform === 'url') {
      return (
        <LocalVideo
          ref={videoRef}
          src={platform === 'local' ? `/videos/${videoId}` : videoId}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          isDebugging={showDebug}
        />
      );
    } else {
      const embedUrl = getEmbedUrl();
      return (
        <VideoIframe
          src={embedUrl}
          allow="autoplay; fullscreen; encrypted-media"
          allowFullScreen
          onError={() => {
            console.log('Video failed to load, using fallback');
          }}
        />
      );
    }
  };

  return (
    <Background>
      {showDebug && (
        <DebugInfo>
          Video Debug Info:<br/>
          Platform: {platform}<br/>
          Video ID: {videoId}<br/>
          Status: {debugInfo}<br/>
          URL: /videos/{videoId}
        </DebugInfo>
      )}
      <VideoContainer>
        {renderVideo()}
      </VideoContainer>
      {/* Fallback background if video fails */}
      <FallbackBackground style={{ 
        backgroundImage: fallbackImage ? `url(${fallbackImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} />
    </Background>
  );
};

export default VideoBackground; 